import { useState } from 'react';
import { Calendar, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AppointmentModal } from '../components/AppointmentModal';
import { NavBar } from '../components/NavBar';
import { Footer } from '../components/Footer';
import { PageHead } from '../components/PageHead';
import { StructuredData, breadcrumb } from '../components/StructuredData';
import { doctolibRenewalByAge, DOCTOLIB_RUSSEY_EXAMEN_VUE } from '../doctolibLinks';
import { track } from '../utils/umami';
import '../App.css';

function formatDateInput(value) {
  const n = value.replace(/\D/g, '');
  if (n.length <= 2) return n;
  if (n.length <= 4) return `${n.slice(0, 2)}/${n.slice(2)}`;
  return `${n.slice(0, 2)}/${n.slice(2, 4)}/${n.slice(4, 8)}`;
}

function parseFrenchDate(str) {
  if (!str || str.length < 10) return null;
  const [d, m, y] = str.split('/');
  if (!d || !m || !y) return null;
  return new Date(y, m - 1, d);
}

export function OrdonnancePage() {
  const [birthDate, setBirthDate]           = useState('');
  const [prescriptionDate, setPrescDate]    = useState('');
  const [result, setResult]                 = useState(null);
  const [modalOpen, setModalOpen]           = useState(false);
  const [prefill, setPrefill]               = useState(null);

  const calculate = () => {
    if (!birthDate || !prescriptionDate) {
      setResult({ valid: null, message: 'Veuillez remplir tous les champs.' });
      return;
    }
    const birth = parseFrenchDate(birthDate);
    const presc = parseFrenchDate(prescriptionDate);
    if (!birth || !presc) {
      setResult({ valid: null, message: 'Format de date invalide. Utilisez jj/mm/aaaa.' });
      return;
    }
    const today = new Date();
    const currentAge = Math.floor((today - birth) / (365.25 * 24 * 60 * 60 * 1000));
    const validityYears = currentAge < 16 ? 1 : currentAge <= 42 ? 5 : 3;
    const expiry = new Date(presc);
    expiry.setFullYear(expiry.getFullYear() + validityYears);
    const isValid = today <= expiry;
    const days = Math.floor((expiry - today) / (24 * 60 * 60 * 1000));
    if (isValid) {
      track('verification-ordonnance', { resultat: 'valide', age: currentAge });
      setResult({ valid: true, currentAge, message: `Votre ordonnance est encore valide pour ${days} jours (expire le ${expiry.toLocaleDateString('fr-FR')}).` });
    } else {
      track('verification-ordonnance', { resultat: 'expiree', age: currentAge });
      setResult({ valid: false, currentAge, message: `Votre ordonnance a expiré il y a ${Math.abs(days)} jours (expirée le ${expiry.toLocaleDateString('fr-FR')}).` });
    }
  };

  return (
    <div className="App">
      <PageHead
        title="Validité d'ordonnance – Lunetterie du Russey | Outil gratuit"
        description="Vérifiez en quelques secondes si votre ordonnance de lunettes est encore valable. Outil gratuit, calcul selon les règles légales françaises (1, 3 ou 5 ans selon l'âge)."
      />
      <StructuredData data={breadcrumb([
        { name: 'Accueil', path: '/' },
        { name: "Validité d'ordonnance", path: '/ordonnance' },
      ])} />
      <NavBar />

      <section id="simulateur" className="simulateur-section page-section-first wave-bottom-to-dark" aria-label="Outil de vérification d'ordonnance">
        <div className="section-container">
          <div className="section-header">
            <Badge className="section-badge">Outil Pratique</Badge>
            <h1 className="section-title">Mon Ordonnance Est-Elle Encore Valable ?</h1>
            <p className="section-description">
              Vérifiez en quelques secondes la validité de votre ordonnance ophtalmologique
            </p>
          </div>

          <Card className="simulateur-card">
            <CardContent className="simulateur-content">
              <div className="simulateur-inputs">
                <div className="input-group">
                  <Label htmlFor="simBirthDate">
                    <Calendar className="inline mr-2 h-4 w-4" />
                    Date de naissance
                  </Label>
                  <Input id="simBirthDate" type="text" value={birthDate}
                    onChange={(e) => setBirthDate(formatDateInput(e.target.value))}
                    placeholder="jj/mm/aaaa" inputMode="numeric" maxLength="10" pattern="\d{2}/\d{2}/\d{4}" />
                </div>
                <div className="input-group">
                  <Label htmlFor="simPrescDate">
                    <Calendar className="inline mr-2 h-4 w-4" />
                    Date de l'ordonnance
                  </Label>
                  <Input id="simPrescDate" type="text" value={prescriptionDate}
                    onChange={(e) => setPrescDate(formatDateInput(e.target.value))}
                    placeholder="jj/mm/aaaa" inputMode="numeric" maxLength="10" pattern="\d{2}/\d{2}/\d{4}" />
                </div>
              </div>

              <Button onClick={calculate} className="simulateur-button">Vérifier la validité</Button>

              {result && (
                <div className={`result-card ${result.valid === true ? 'valid' : result.valid === false ? 'invalid' : 'neutral'}`}>
                  {result.valid === true  && <CheckCircle2 className="result-icon" />}
                  {result.valid === false && <XCircle className="result-icon" />}
                  <p className="result-message">{result.message}</p>

                  {result.valid === false && (() => {
                    const { href, label } = doctolibRenewalByAge(result.currentAge);
                    return (
                      <>
                        <Button variant="outline" className="renew-button" onClick={() => {
                          track('ouverture-modal-rdv', { source: 'ordonnance-expiree' });
                          setPrefill({ birthDate, prescriptionDate });
                          setModalOpen(true);
                        }}>
                          Prendre RDV pour renouveler
                        </Button>
                        <Button asChild className="renew-button bg-rust hover:bg-rust-light text-white mt-2">
                          <a href={href} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2" onClick={() => track('clic-doctolib', { type: 'renouvellement-ordonnance' })}>
                            <ExternalLink className="h-4 w-4" />
                            {label}
                          </a>
                        </Button>
                      </>
                    );
                  })()}

                  {result.valid === true && (
                    <div className="w-full max-w-md mx-auto flex flex-col gap-3 mt-1">
                      <Button type="button" className="simulateur-cta-choix-lunettes"
                        onClick={() => { track('ouverture-modal-rdv', { source: 'ordonnance-valide' }); setPrefill({ birthDate, prescriptionDate }); setModalOpen(true); }}>
                        Rendez-vous : choix de lunettes
                      </Button>
                      <Button asChild variant="outline" className="renew-button border-2 border-anthracite w-full">
                        <a href={DOCTOLIB_RUSSEY_EXAMEN_VUE} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2" onClick={() => track('clic-doctolib', { type: 'examen-vue-russey' })}>
                          <ExternalLink className="h-4 w-4" />
                          Examen de vue (Doctolib)
                        </a>
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="simulateur-info">
                <p className="text-sm text-muted-foreground">
                  <strong>Règles de validité :</strong> 1 an pour les moins de 16 ans · 5 ans entre 16 et 42 ans · 3 ans au-delà de 42 ans
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />

      <AppointmentModal
        open={modalOpen}
        onOpenChange={(o) => { setModalOpen(o); if (!o) setPrefill(null); }}
        prefillData={prefill}
      />
    </div>
  );
}
